/**
 * Zone Instances Tab Component
 *
 * Displays a table of VM instances for the current zone.
 * Rows are clickable to navigate to instance detail.
 */

import { useParams, useNavigate } from 'react-router-dom'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getZoneInstances } from '../../../services/capacity'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'

/** Get status badge props */
const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
        case 'running':
            return {
                variant: 'default' as const,
                className: 'bg-green-100 text-green-700 border-green-200',
                label: 'Running',
            }
        case 'stopped':
            return {
                variant: 'secondary' as const,
                className: 'bg-gray-100 text-gray-700 border-gray-200',
                label: 'Stopped',
            }
        case 'suspended':
            return {
                variant: 'outline' as const,
                className: 'bg-amber-100 text-amber-700 border-amber-200',
                label: 'Suspended',
            }
        case 'starting':
        case 'stopping':
            return {
                variant: 'outline' as const,
                className: 'bg-blue-100 text-blue-700 border-blue-200',
                label: status.charAt(0).toUpperCase() + status.slice(1),
            }
        case 'error':
            return {
                variant: 'destructive' as const,
                className: 'bg-red-100 text-red-700 border-red-200',
                label: 'Error',
            }
        default:
            return {
                variant: 'outline' as const,
                className: '',
                label: status || 'Unknown',
            }
    }
}

const ZoneInstances = () => {
    const { zoneId } = useParams<{ zoneId: string }>()
    const navigate = useNavigate()

    const { data: instances, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.ZONE_INSTANCES, zoneId],
        queryFn: () => getZoneInstances(zoneId!),
        enabled: !!zoneId,
    })

    const handleRowClick = (instanceUid: string) => {
        // Navigate using UID since capacity API returns live provider data
        navigate(`/warehouse/cloud/instances/${instanceUid}`)
    }

    if (isLoading) {
        return (
            <Card className="mt-2">
                <CardHeader>
                    <CardTitle>Instances</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-2">
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} className="h-12 w-full" />
                        ))}
                    </div>
                </CardContent>
            </Card>
        )
    }

    if (!instances || instances.length === 0) {
        return (
            <Card className="mt-2">
                <CardHeader>
                    <CardTitle>Instances</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-center py-8 text-muted-foreground">
                        No instances found in this zone
                    </div>
                </CardContent>
            </Card>
        )
    }

    return (
        <Card className="mt-2">
            <CardHeader>
                <CardTitle>Instances ({instances.length})</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>UID</TableHead>
                                <TableHead className="text-right">
                                    CPU
                                </TableHead>
                                <TableHead className="text-right">
                                    Memory
                                </TableHead>
                                <TableHead className="text-right">
                                    Disk
                                </TableHead>
                                <TableHead className="text-center">
                                    Status
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {instances.map((instance) => {
                                const statusBadge = getStatusBadge(
                                    instance.status
                                )
                                const memoryGB = instance.spec?.memory_mb
                                    ? (instance.spec.memory_mb / 1024).toFixed(
                                          1
                                      )
                                    : '-'

                                return (
                                    <TableRow
                                        key={instance.uid}
                                        className="cursor-pointer hover:bg-muted/50"
                                        onClick={() =>
                                            handleRowClick(instance.uid)
                                        }
                                    >
                                        <TableCell className="font-medium">
                                            {instance.name}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground text-sm">
                                            {instance.uid}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {instance.spec?.cpu || '-'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {memoryGB !== '-'
                                                ? `${memoryGB} GB`
                                                : '-'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {instance.spec?.disk_gb
                                                ? `${instance.spec.disk_gb} GB`
                                                : '-'}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <Badge
                                                variant={statusBadge.variant}
                                                className={
                                                    statusBadge.className
                                                }
                                            >
                                                {statusBadge.label}
                                            </Badge>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    )
}

export default ZoneInstances
